import { ActivityMode, ContentType } from '@/models/Session';

import { ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { startActivityRequest } from '@/utils/ghcc';

import { useGhccSessionStore } from './useGhccSessionStore';
import { useGhccIframeStore } from './useGhccIframeStore';
import { useToastStore } from './useToastStore';
import { useWebSessionStore } from './useWebSessionStore';
import { useSessionStore } from './useSessionStore';

export const useGhccActivityStore = defineStore('ghccActivity', () => {
  const route = useRoute();

  const { t: translate } = useI18n();

  const isLoading = ref<boolean>(false);
  const isActivityInitialized = ref<boolean>(false);
  const isActivityStarted = ref<boolean>(false);

  const { isConnected, sessionId } = storeToRefs(useGhccSessionStore());

  const iframeStore = useGhccIframeStore();
  const { lastActivityId } = storeToRefs(iframeStore);
  const { sendStartActivityMessage } = iframeStore;

  const { updateWebsessionRunningCoursesList } = useWebSessionStore();
  const { showToast } = useToastStore();

  const sessionStore = useSessionStore();
  const { isLms, contentType, activityMode } = storeToRefs(sessionStore);

  iframeStore.$subscribe((_, { lastActivityId }) => {
    checkLastActivity(lastActivityId as string);
  });

  const initializeActivity = async () => {
    if (
      !route.params.activityId ||
      !isConnected.value ||
      isLoading.value ||
      isActivityInitialized.value ||
      isActivityStarted.value
    )
      return;

    try {
      isLoading.value = true;

      await startActivityRequest(route.params.activityId as string, sessionId.value as string);

      isActivityInitialized.value = true;

      sendStartActivityMessage(route.params.activityId as string);

      checkLastActivity(lastActivityId.value as string);
    } catch (error) {
      isActivityInitialized.value = false;

      showToast({
        title: translate('toast.error'),
        message: translate('toast.cannot_start_activity'),
      });
    } finally {
      isLoading.value = false;
    }
  };

  const checkLastActivity = (activityId: string) => {
    if (!activityId || !route.params.activityId) return;

    if (activityId.toLowerCase() === (route.params.activityId as string).toLowerCase()) {
      startActivity();
    }
  };

  const startActivity = () => {
    if (!isActivityInitialized.value) return;

    isLoading.value = false;
    isActivityStarted.value = true;

    if (
      !isLms.value &&
      contentType.value === ContentType.course &&
      activityMode.value === ActivityMode.normal
    ) {
      updateWebsessionRunningCoursesList(sessionStore.contentId as number);
    }
  };

  const terminateActivity = () => {
    isActivityInitialized.value = false;
    isActivityStarted.value = false;
  };

  return {
    isLoading,
    isActivityInitialized,
    isActivityStarted,
    initializeActivity,
    startActivity,
    terminateActivity,
  };
});
